import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SyncService, SyncStatus } from '@vex/http-service/sync.service';
import { MatDialog } from '@angular/material/dialog';
import { SyncStatusDialogComponent } from '@vex/layout/toolbar/toolbar-user/sync-status-dialog.component';

@Component({
  selector: 'app-sync-status-popup',
  templateUrl: './sync-status-popup.component.html',
  styleUrls: ['./sync-status-popup.component.scss']
})
export class SyncStatusPopupComponent implements OnInit, OnDestroy {
  status: SyncStatus;
  visible = false;
  retrying = false;
  private tableCount = 7;
  private readonly tableStatuses: { [tableName: string]: SyncStatus } = {};
  private subscription: Subscription;
  private hideTimer: any;
  private hydrationVersion = 0;
  private readonly tableStatusVersions: { [tableName: string]: number } = {};

  constructor(private syncService: SyncService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.hydrateFromDiagnostics();
    this.subscription = this.syncService.syncStatus$.subscribe(status => {
      if (status.reset) {
        this.hydrationVersion++;
        Object.keys(this.tableStatuses).forEach(tableName => delete this.tableStatuses[tableName]);
        this.status = null;
        this.visible = false;
        this.tableCount = 7;
        return;
      }
      this.status = status;
      if (status.tableName) {
        this.tableStatusVersions[status.tableName] = (this.tableStatusVersions[status.tableName] || 0) + 1;
        const previous = this.tableStatuses[status.tableName] || {} as SyncStatus;
        this.tableStatuses[status.tableName] = {
          ...previous,
          ...status,
          // A new attempt must not inherit Ready/Failed flags from the table's
          // previous pass. Those flags otherwise make active work count as done.
          tableCompleted: status.tableCompleted === true
            ? true
            : (status.active ? false : previous.tableCompleted),
          error: status.error === true
            ? true
            : (status.active ? false : previous.error)
        };
      }
      this.visible = status.active || status.completed || status.error || this.failedTables > 0;

      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
      }

      if (this.completedTables === this.totalTables && this.failedTables === 0) {
        this.hideTimer = setTimeout(() => {
          this.visible = false;
        }, 4000);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }
  }

  /** Number of platform-data tables participating in the current sync pass. */
  get totalTables(): number {
    return this.status?.totalTables || this.tableCount;
  }

  /** Number of tables that have passed validation and committed a checkpoint. */
  get completedTables(): number {
    return Object.keys(this.tableStatuses)
      .filter(tableName => this.tableStatuses[tableName].tableCompleted)
      .length;
  }

  /** Number of tables whose latest attempt failed. */
  get failedTables(): number {
    return Object.keys(this.tableStatuses)
      .filter(tableName => this.tableStatuses[tableName].error && !this.tableStatuses[tableName].tableCompleted)
      .length;
  }

  /** Weighted workspace progress based on every table's fixed-snapshot count. */
  get progress(): number {
    const tableProgress = Object.keys(this.tableStatuses).reduce((total, tableName) => {
      const table = this.tableStatuses[tableName];
      if (table.tableCompleted) {
        return total + 1;
      }
      if (table.expectedItemCount && table.expectedItemCount > 0) {
        return total + Math.min(0.99, (table.returnedItemCount || 0) / table.expectedItemCount);
      }
      return total;
    }, 0);

    return Math.min(100, Math.round((tableProgress / this.totalTables) * 100));
  }

  /** Most recent active table, falling back to the latest sync event. */
  get currentTable(): SyncStatus {
    if (this.status?.active && !this.status.tableCompleted && !this.status.error) {
      return this.status;
    }
    const activeTableName = Object.keys(this.tableStatuses)
      .find(tableName => this.tableStatuses[tableName].active &&
        !this.tableStatuses[tableName].tableCompleted && !this.tableStatuses[tableName].error);
    return activeTableName ? this.tableStatuses[activeTableName] : this.status;
  }

  /** Percentage of changed records received for the currently displayed table. */
  get currentTableProgress(): number {
    const table = this.currentTable;
    if (!table) {
      return 0;
    }
    if (table.tableCompleted) {
      return 100;
    }
    return table.expectedItemCount && table.expectedItemCount > 0
      ? Math.min(99, Math.round(((table.returnedItemCount || 0) / table.expectedItemCount) * 100))
      : 0;
  }

  /** Seeds replacement tabs from the same durable checkpoints shown in details. */
  private async hydrateFromDiagnostics(): Promise<void> {
    const hydrationVersion = this.hydrationVersion;
    const tableStatusVersions = { ...this.tableStatusVersions };
    try {
      const diagnostics = await this.syncService.getDiagnostics();
      // A clear-data logout may finish while IndexedDB diagnostics are being
      // read. Never let that stale read repopulate the notification model.
      if (hydrationVersion !== this.hydrationVersion) {
        return;
      }
      this.tableCount = diagnostics.tableCount || this.tableCount;
      diagnostics.tables.forEach(table => {
        // Preserve a live event that arrived for this table while diagnostics
        // were being read; other tables can still use the durable baseline.
        if ((this.tableStatusVersions[table.tableName] || 0) !== (tableStatusVersions[table.tableName] || 0)) {
          return;
        }
        this.tableStatuses[table.tableName] = {
          active: table.status === 'Syncing' || table.status === 'Waiting',
          initialSync: !table.committedThrough,
          tableName: table.tableName,
          tableLabel: table.tableLabel,
          message: table.message || '',
          returnedItemCount: table.recordCount,
          expectedItemCount: table.serverRecordCount,
          totalTables: diagnostics.tableCount,
          tableCompleted: table.status === 'Ready',
          error: table.status === 'Failed' || table.status === 'Repair needed'
        };
      });
    } catch (error) {
      console.warn('Unable to seed workspace sync progress from IndexedDB.', error);
    }
  }

  /** Opens the full table-by-table IndexedDB and synchronization diagnostics. */
  openDetails(): void {
    this.dialog.open(SyncStatusDialogComponent, {
      width: '1120px',
      maxWidth: '96vw',
      maxHeight: '90vh'
    });
  }

  /** Retries only tables that do not currently own a validated Ready checkpoint. */
  async retry(): Promise<void> {
    if (this.retrying) {
      return;
    }
    this.retrying = true;
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }
    try {
      await this.syncService.retryIncompleteTables();
    } finally {
      this.retrying = false;
    }
  }

  dismiss(): void {
    this.visible = false;
  }
}
